import { defineStore } from 'pinia';
import { api } from '../lib/api';
import { workspaceTimelineActions } from './workspaceTimelineActions';
import { workspaceSceneEditActions } from './workspaceSceneEditActions';
import { workspaceVersionHistoryActions } from './workspaceVersionHistoryActions';
import { workspaceConfigurationActions } from './workspaceConfigurationActions';
import { workspaceEntityCacheActions, type EntityTabCache } from './workspaceEntityCacheActions';
import { workspaceEntityActions } from './workspaceEntityActions';
import { workspaceDraftActions } from './workspaceDraftActions';
import { workspaceCatalogActions } from './workspaceCatalogActions';
import { getWorkspaceSessionGeneration, workspaceSessionActions } from './workspaceSessionActions';
import type { Backlink, CategoryCollection, CategoryField, EntityCategory, EntityDocument, EntityFieldHistoryEntry, EntitySummary, EntityTag, EntityTemplate, ExplorerNode, PrototypeBlock, ResolvedEntityFieldValues } from '../types/entities';
import type { BootstrapResponse, WorkspaceSaveState, WorkspaceSummary } from '../types/workspace';
import type { CharacterHistoryEntry } from '../types/relationships';
import type { CharacterHistoryTimelineOptions, CharacterTimelineData, TimelineEra, TimelineOccurrence } from '../types/timeline';
import type { SceneConflict, SceneSegment, SceneSegmentField } from '../types/scenes';

export type SaveState = WorkspaceSaveState;
export type EmbeddedWorkspaceSeed = BootstrapResponse & {
  entities: EntitySummary[];
  timelineOccurrences: TimelineOccurrence[];
  timelineEras: TimelineEra[];
  characterTimeline: CharacterTimelineData | null;
};

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    workspace: null as WorkspaceSummary | null,
    availableWorkspaces: [] as WorkspaceSummary[],
    archivedWorkspaces: [] as WorkspaceSummary[],
    templates: [] as EntityTemplate[],
    categories: [] as EntityCategory[],
    categoryCollections: [] as CategoryCollection[],
    categoryFields: [] as CategoryField[],
    entities: [] as EntitySummary[],
    tags: [] as EntityTag[],
    activeEntityTags: [] as EntityTag[],
    explorerNodes: [] as ExplorerNode[],
    prototypeBlocks: [] as PrototypeBlock[],
    timelineOccurrences: [] as TimelineOccurrence[],
    timelineEras: [] as TimelineEra[],
    characterTimeline: null as CharacterTimelineData | null,
    characterHistory: [] as CharacterHistoryEntry[],
    characterHistoryLoaded: false,
    entityFieldHistory: [] as EntityFieldHistoryEntry[],
    globalTimeContext: { sceneId: null, segmentId: null } as { sceneId: string | null; segmentId: string | null },
    resolvedEntityFieldValues: null as ResolvedEntityFieldValues | null,
    characterHistoryTimelineOptions: { chapters: [], scenes: [], segments: [], states: [], suggestions: [] } as CharacterHistoryTimelineOptions,
    activeEntity: null as EntityDocument | null,
    backlinks: [] as Backlink[],
    sceneSegments: [] as SceneSegment[],
    sceneSegmentFields: [] as SceneSegmentField[],
    sceneConflicts: [] as SceneConflict[],
    entityTabCache: new Map<string, EntityTabCache>(),
    loading: true,
    saveState: 'clean' as SaveState,
    error: ''
  }),
  getters: {
    activeTemplate(state): EntityTemplate | undefined {
      const template = state.templates.find((template) => template.id === state.activeEntity?.templateId);
      return template ? { ...template, fields: [...template.fields, ...(state.activeEntity?.entityFields ?? [])] } : undefined;
    }
  },
  actions: {
    ...workspaceTimelineActions,
    ...workspaceSceneEditActions,
    ...workspaceVersionHistoryActions,
    ...workspaceConfigurationActions,
    ...workspaceEntityCacheActions,
    ...workspaceCatalogActions,
    ...workspaceEntityActions,
    ...workspaceDraftActions,
    ...workspaceSessionActions,
    clearEntityTabCache() { this.entityTabCache.clear(); },
    async loadWorkspaceList(includeArchived = false) {
      const generation = getWorkspaceSessionGeneration();
      const response = await api<{ items: WorkspaceSummary[] }>(`/api/v1/workspaces${includeArchived ? '?includeArchived=true' : ''}`);
      if (generation !== getWorkspaceSessionGeneration()) return [];
      this.availableWorkspaces = response.items.filter((item) => !item.archivedAt);
      if (includeArchived) this.archivedWorkspaces = response.items.filter((item) => Boolean(item.archivedAt));
      return response.items;
    },
    async initialize(workspaceId?: string, options: { embedded?: boolean; seed?: EmbeddedWorkspaceSeed | null } = {}) {
      const generation = getWorkspaceSessionGeneration();
      this.loading = true;
      this.error = '';
      try {
        if (!options.embedded || !workspaceId) await this.loadWorkspaceList();
        if (generation !== getWorkspaceSessionGeneration()) return;
        const storedWorkspaceId = localStorage.getItem('chronis.active-workspace.v1');
        const requestedWorkspaceId = workspaceId ?? (this.availableWorkspaces.some((item) => item.id === storedWorkspaceId) ? storedWorkspaceId ?? undefined : undefined) ?? this.availableWorkspaces[0]?.id;
        if (!requestedWorkspaceId) throw new Error('Dir ist noch keine Welt zugeordnet.');
        const seed = options.seed?.workspace.id === requestedWorkspaceId ? options.seed : null;
        const bootstrap = seed ?? await api<BootstrapResponse>(`/api/v1/bootstrap?workspaceId=${encodeURIComponent(requestedWorkspaceId)}`);
        if (generation !== getWorkspaceSessionGeneration()) return;
        this.workspace = bootstrap.workspace;
        localStorage.setItem('chronis.active-workspace.v1', bootstrap.workspace.id);
        this.templates = bootstrap.templates;
        this.categories = bootstrap.categories;
        this.categoryCollections = bootstrap.categoryCollections;
        this.categoryFields = bootstrap.categoryFields;
        if (seed) {
          this.availableWorkspaces = [seed.workspace];
          this.entities = seed.entities;
          this.timelineOccurrences = seed.timelineOccurrences;
          this.timelineEras = seed.timelineEras;
          this.characterTimeline = seed.characterTimeline;
        } else if (options.embedded) await this.loadEntities();
        else await Promise.all([this.loadEntities(), this.loadTags(), this.loadExplorer(), this.loadTimeline(), this.loadCharacterTimeline()]);
        if (generation !== getWorkspaceSessionGeneration()) return;
        this.restoreGlobalTimeContext();
        const idFromPath = location.pathname.match(/\/e\/([0-9a-f-]{36})/i)?.[1];
        const initial = this.entities.find((entity) => entity.id === idFromPath) ?? this.entities[0];
        if (initial) await this.openEntity(initial.id, false);
        if (options.embedded && !seed) void Promise.all([this.loadTimeline(), this.loadCharacterTimeline()]).catch(() => undefined);
      } catch (error) {
        if (generation === getWorkspaceSessionGeneration()) this.error = error instanceof Error ? error.message : 'Chronis konnte nicht geladen werden.';
      } finally {
        if (generation === getWorkspaceSessionGeneration()) this.loading = false;
      }
    },
  }
});
